import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Search, FileText, FolderOpen, Database, Clock,
  Loader2, ArrowRight, X, Command
} from "lucide-react";
import { globalSearch } from "@/lib/api";

const RECENT_SEARCHES_KEY = "les_lex_recent_searches";
const MAX_RECENT = 5;

const typeConfig = {
  project: { icon: FolderOpen, label: "Projets", color: "text-blue-600 bg-blue-50" },
  source: { icon: FileText, label: "Sources", color: "text-amber-600 bg-amber-50" },
  knowledge: { icon: Database, label: "Base de connaissances", color: "text-emerald-600 bg-emerald-50" },
};

function getRecentSearches() {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentSearch(query) {
  if (!query.trim()) return;
  const recent = getRecentSearches().filter((q) => q !== query.trim());
  recent.unshift(query.trim());
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Load recent searches on open
  useEffect(() => {
    if (open) {
      setRecentSearches(getRecentSearches());
      setQuery("");
      setResults(null);
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await globalSearch(searchQuery.trim());
      setResults(res.data);
      setSelectedIndex(0);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim() || query.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, performSearch]);

  // Flatten results for keyboard navigation
  const flatResults = [];
  if (results) {
    for (const type of ["project", "source", "knowledge"]) {
      const items = results[type] || results[`${type}s`] || [];
      items.forEach((item) => flatResults.push({ ...item, _type: type }));
    }
  }

  const navigateToResult = (item) => {
    saveRecentSearch(query);
    setOpen(false);
    const type = item._type || item.type;
    if (type === "project") {
      navigate(`/project/${item.id}/creator`);
    } else if (type === "source") {
      navigate(`/project/${item.project_id}/sources`);
    } else if (type === "knowledge") {
      navigate("/knowledge-base");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, flatResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flatResults.length > 0 && flatResults[selectedIndex]) {
        navigateToResult(flatResults[selectedIndex]);
      }
    }
  };

  const handleRecentClick = (recentQuery) => {
    setQuery(recentQuery);
  };

  // Group results by type for display
  const groupedResults = {};
  if (results) {
    for (const type of ["project", "source", "knowledge"]) {
      const items = results[type] || results[`${type}s`] || [];
      if (items.length > 0) {
        groupedResults[type] = items.map((item) => ({ ...item, _type: type }));
      }
    }
  }

  let globalIndex = 0;

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-muted/50 text-sm text-muted-foreground hover:bg-muted transition-colors"
      >
        <Search className="h-4 w-4" />
        <span className="hidden sm:inline">Rechercher...</span>
        <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border bg-background px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
          <Command className="h-3 w-3" />K
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[540px] p-0 gap-0 overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Rechercher projets, sources, connaissances..."
              className="h-12 border-0 shadow-none focus-visible:ring-0 px-0 text-sm"
            />
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />}
            {query && !loading && (
              <button onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <ScrollArea className="max-h-[400px]">
            {/* Recent searches (when no query) */}
            {!query.trim() && recentSearches.length > 0 && (
              <div className="p-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 mb-2">
                  Recherches récentes
                </p>
                {recentSearches.map((recent, i) => (
                  <button
                    key={i}
                    onClick={() => handleRecentClick(recent)}
                    className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                  >
                    <Clock className="h-4 w-4 shrink-0" />
                    <span className="truncate text-left">{recent}</span>
                  </button>
                ))}
              </div>
            )}

            {/* No query, no recent */}
            {!query.trim() && recentSearches.length === 0 && (
              <div className="p-8 text-center">
                <Search className="h-8 w-8 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Entrez un terme pour rechercher...
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Utilisez les flèches et Entrée
                </p>
              </div>
            )}

            {/* Search results */}
            {query.trim().length >= 2 && !loading && flatResults.length === 0 && results !== null && (
              <div className="p-8 text-center">
                <Search className="h-8 w-8 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Aucun résultat pour "{query}"</p>
                <p className="text-xs text-muted-foreground mt-1">Essayez un autre terme</p>
              </div>
            )}

            {Object.keys(groupedResults).length > 0 && (
              <div className="p-2">
                {Object.entries(groupedResults).map(([type, items], groupIdx) => {
                  const config = typeConfig[type] || typeConfig.project;
                  const TypeIcon = config.icon;
                  return (
                    <div key={type}>
                      {groupIdx > 0 && <Separator className="my-1" />}
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 py-2">
                        {config.label}
                      </p>
                      {items.map((item) => {
                        const currentIndex = globalIndex++;
                        const isSelected = currentIndex === selectedIndex;
                        return (
                          <button
                            key={`${type}-${item.id || currentIndex}`}
                            onClick={() => navigateToResult(item)}
                            className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm transition-colors ${
                              isSelected
                                ? "bg-accent text-accent-foreground"
                                : "text-foreground hover:bg-muted"
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${config.color}`}>
                              <TypeIcon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0 text-left">
                              <p className="font-medium text-sm truncate">{item.name || item.title}</p>
                              {item.description && (
                                <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                              )}
                            </div>
                            <Badge variant="outline" className="text-[10px] shrink-0">
                              {config.label}
                            </Badge>
                            {isSelected && <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t bg-muted/30 text-[10px] text-muted-foreground">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-background px-1 py-0.5 font-mono">&#8593;&#8595;</kbd> nawigacja
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-background px-1 py-0.5 font-mono">Enter</kbd> otworz
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border bg-background px-1 py-0.5 font-mono">Esc</kbd> zamknij
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
